import {useRouteMatch} from "react-router-dom";
import { PdfReadySignal } from "./components/pdfReadySignal";
import {LaegekompetenceMeta} from "../../sharedmodels/kompetence/laegekompetenceMeta";
import {useEffect, useState} from "react";
import {LaegekompetenceStatusEnum} from "../../sharedmodels/kompetence/laegekompetenceStatusEnum";
import {Localizer} from "../../../infrastructure/localization/localizer";
import PdfComponent from "core/components/pdf/components/pdfComponent";
import {PdfKompetenceHeaderComponent} from "./pdfHeaders/pdfKompetenceHeaderComponent";
import {useFetchWithErrorHandlingPdf} from "./hooks/useFetchWithErrorHandlingPdf";
import useLogbogApi from "../../hooks/useLogbogApi";
import useStamdataApi from "../../hooks/useStamdataApi";
import {PdfKompetenceHeaderModel} from "./models/pdfKompetenceHeaderModel";
import { fetchKompetenceHeader } from "./helpers/pdfFetchers";

export function KompetencePdf() {
    const routeMatch = useRouteMatch();
    const routeParams = routeMatch.params as { id: string, brugerId: string, brugerMaalbeskrivelseId: string };

    const [laegeKompetence, setLaegeKompetence] = useState<LaegekompetenceMeta>();
    const [headerData, setHeaderData] = useState<PdfKompetenceHeaderModel>();
    const [dataLoaded, setDataLoaded] = useState(false);
    const setError = useFetchWithErrorHandlingPdf();
    const { logbogLaegeKompetenceApi, logbogUserInfoApi } = useLogbogApi();
    const { stamdataMaalbeskrivelseApi, stamdataSpecialeApi } = useStamdataApi();

    useEffect(() => {
        const fetchData = async () => {
            const [kompetence, header] = await Promise.all([
                logbogLaegeKompetenceApi.getLaegekompetenceById(routeParams.id, routeParams.brugerId),
                fetchKompetenceHeader(routeParams.brugerId, routeParams.brugerMaalbeskrivelseId, { stamdataMaalbeskrivelseApi, stamdataSpecialeApi, logbogUserInfoApi })
            ]);
            setLaegeKompetence(kompetence);
            setHeaderData(header);
            setDataLoaded(true);
        };
        fetchData().catch(setError);
    }, [routeParams.id, routeParams.brugerId, routeParams.brugerMaalbeskrivelseId]);

    return (
        <PdfComponent>
            {laegeKompetence && headerData &&
                <>
                    <PdfKompetenceHeaderComponent
                        brugerId={routeParams.brugerId}
                        data={headerData}
                    />
                    <PdfComponent.BlockItem className="card shadow-none">
                        <PdfComponent.CardHeader>
                            <h4 className="card-title">{laegeKompetence.fullKompetenceTitleWithNumber}</h4>
                        </PdfComponent.CardHeader>
                        <PdfComponent.CardBody>
                            <div className="margin-m">
                                <div className="margin-bottom-m">
                                    <h5>{Localizer.global_kompetenceTitle()}</h5>
                                    <div>{laegeKompetence.fullKompetenceTitle}</div>
                                    <div className={"subtle padding-top-0 margin-top-0"}>{laegeKompetence.underoverskrift}</div>
                                </div>

                                <div className="margin-bottom-m">
                                    <h5>{Localizer.global_kompetenceNummer()}</h5>
                                    <p>{laegeKompetence.kompetenceNummer}</p>
                                </div>

                                <div className="margin-bottom-m">
                                    <h5>{Localizer.global_konkretisering()}</h5>
                                    <p>{laegeKompetence.konkretisering}</p>
                                </div>

                                <div className="margin-bottom-m">
                                    <h5>{Localizer.global_teachingStrategy()}</h5>
                                    <p className="kompetence-full-text">{laegeKompetence.laeringsstrategi}</p>
                                </div>

                                <div className="margin-bottom-m">
                                    <h5>{Localizer.global_kompetenceVurderingsMetoder()}</h5>
                                    <p className="kompetence-full-text">{laegeKompetence.kompetenceVurderingsmetoder}</p>
                                </div>

                                <div className="margin-bottom-m">
                                    <h5>{Localizer.global_laegeroller()}</h5>
                                    {laegeKompetence.laegeroller.split(";").map((rolle) =>
                                        <p key={rolle}>{rolle}</p>
                                    )}
                                </div>

                                <div>
                                    <h5>{Localizer.global_status()}</h5>
                                    {laegeKompetence.status === LaegekompetenceStatusEnum.Godkendt &&
                                        <div className={"flex-container"}>
                                            {Localizer.kompetencePdf_Godkendt((laegeKompetence.godkendtDato as Date).dateWithoutTimeFormat(false), laegeKompetence.godkendtAfNavn)}
                                            {laegeKompetence.godkendtAfAuthId &&
                                                <p>
                                                    &nbsp; ({Localizer.global_authorisationId()}: {laegeKompetence.godkendtAfAuthId})
                                                </p>}
                                        </div>
                                    }
                                    {laegeKompetence.status !== LaegekompetenceStatusEnum.Godkendt &&
                                        <p>{LaegekompetenceStatusEnum[laegeKompetence.status]}</p>
                                    }
                                </div>

                                <div className="margin-bottom-m">
                                    <h5>{Localizer.forloebOverviewPage_fase()}</h5>
                                    <p className="kompetence-full-text">{laegeKompetence.fase ?? Localizer.kompetencePage_ingenFaseAngivet()}</p>
                                </div>
                            </div>
                        </PdfComponent.CardBody>
                    </PdfComponent.BlockItem>
                </>
            }
            <PdfReadySignal when={dataLoaded} />
        </PdfComponent>
    );
}
